import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Avatar, Box, Button, Card, CardActionArea, CardContent, Chip, Container, Grid,
  InputAdornment, Skeleton, TextField, Typography,
} from '@mui/material';
import { School, Search as SearchIcon, Star } from '@mui/icons-material';
import { apiListProfessors, apiListCoursesForFilter, ProfessorListItem } from '../../services/api';
import { initials } from '../../utils/strings';

const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';

type CourseOption = { id: string; code: string; title: string };

const Professors: React.FC = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [courseId, setCourseId] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<CourseOption | null>(null);
  const [courseQuery, setCourseQuery] = useState('');
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [items, setItems] = useState<ProfessorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Type-ahead class filter: search server-side as the user types (catalogs run to
  // thousands of courses — a plain dropdown is unusable).
  useEffect(() => {
    let cancelled = false;
    setCoursesLoading(true);
    const t = setTimeout(() => {
      apiListCoursesForFilter(courseQuery.trim())
        .then((r) => { if (!cancelled) setCourses(r); })
        .catch(() => { if (!cancelled) setCourses([]); })
        .finally(() => { if (!cancelled) setCoursesLoading(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [courseQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      apiListProfessors({ q: q.trim() || undefined, courseId: courseId || undefined })
        .then((r) => { if (!cancelled) { setItems(r.items || []); setNextCursor(r.nextCursor ?? null); } })
        .catch(() => { if (!cancelled) { setItems([]); setNextCursor(null); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, courseId]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await apiListProfessors({ q: q.trim() || undefined, courseId: courseId || undefined, cursor: nextCursor });
      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...(r.items || []).filter((p) => !seen.has(p.id))];
      });
      setNextCursor(r.nextCursor ?? null);
    } catch { /* keep current list on failure */ }
    finally { setLoadingMore(false); }
  };

  return (
    // disableGutters + py:1: Layout already wraps pages in a padded Container.
    <Container maxWidth="lg" disableGutters sx={{ py: 1 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Professors</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Browse professors at your school, see student ratings, and check their official Rate My Professors page.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search professors…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        <Autocomplete<CourseOption>
          sx={{ minWidth: 280 }}
          options={courses}
          value={selectedCourse}
          loading={coursesLoading}
          onChange={(_, v) => { setSelectedCourse(v); setCourseId(v?.id || ''); }}
          onInputChange={(_, v, reason) => { if (reason !== 'reset') setCourseQuery(v); }}
          getOptionLabel={(c) => `${c.code} — ${c.title}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(x) => x} // options are already server-filtered by courseQuery
          noOptionsText={courseQuery.trim() ? 'No classes match' : 'Type to search classes…'}
          renderInput={(params) => <TextField {...params} label="Filter by class" placeholder="e.g. CS 171" />}
          clearOnBlur={false}
        />
      </Box>

      {loading && items.length === 0 ? (
        <Grid container spacing={2}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}><Skeleton variant="rounded" height={190} /></Grid>
          ))}
        </Grid>
      ) : !loading && items.length === 0 ? (
        <Typography color="text.secondary">No professors found{q || courseId ? ' for this filter' : ' yet'}.</Typography>
      ) : (
        <>
        {/* Keep stale results visible (dimmed) while a refetch is in flight — swapping the
            whole grid to skeletons on every keystroke made the page strobe while typing. */}
        <Grid container spacing={2} sx={{ opacity: loading ? 0.55 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity .15s' }}>
          {items.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea onClick={() => navigate(`/professors/${p.id}`)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Avatar sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700, width: 44, height: 44, flexShrink: 0 }}>
                        {initials(p.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.2 }}>{p.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'secondary.main', flexShrink: 0 }}>
                            <Star fontSize="small" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{p.rmpRating ?? p.avgRating ?? '—'}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mb: 1 }}>
                          <School fontSize="small" />
                          <Typography variant="body2">{p.department || 'Faculty'}</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {p.rmpRating != null ? `RMP ${p.rmpRating}★ (${p.rmpNumRatings ?? 0}) · ` : ''}
                      {p.numReviews} student review{p.numReviews === 1 ? '' : 's'}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {p.courseCodes.slice(0, 4).map((c) => <Chip key={c} label={c} size="small" />)}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        {nextCursor && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load more'}</Button>
          </Box>
        )}
        </>
      )}
    </Container>
  );
};

export default Professors;
